import { useCallback } from 'react';
import { useMutationMarkConversationRead } from '@src/queries/messaging/useMutationMarkConversationRead';
import { useMutationToggleConversationFavorite } from '@src/queries/messaging/useMutationToggleConversationFavorite';

/**
 * Hook to manage conversation read status and favorites using backend APIs
 * This replaces the localStorage-based approach with server-side storage
 */
export function useConversationActions() {
  const markReadMutation = useMutationMarkConversationRead();
  const toggleFavoriteMutation = useMutationToggleConversationFavorite();

  // Mark conversation as read
  const markConversationAsRead = useCallback(
    async (targetId: string, interlocutorId: string) => {
      try {
        await markReadMutation.mutateAsync({
          networkName: 'instagram',
          networkSendersId: interlocutorId, // The user who sent the message
          networkReceiverId: targetId, // Your Instagram account
        });
      } catch (error) {
        console.error('Failed to mark conversation as read:', error);
        throw error;
      }
    },
    [markReadMutation]
  );

  // Toggle conversation favorite status
  const toggleConversationFavorite = useCallback(
    async (targetId: string, interlocutorId: string, isFavorite: boolean) => {
      try {
        await toggleFavoriteMutation.mutateAsync({
          networkName: 'instagram',
          networkSendersId: interlocutorId,
          networkReceiverId: targetId,
          isFavorite,
        });
      } catch (error) {
        console.error('Failed to toggle conversation favorite:', error);
        throw error;
      }
    },
    [toggleFavoriteMutation]
  );

  return {
    markConversationAsRead,
    toggleConversationFavorite,
    isMarkingRead: markReadMutation.isPending,
    isTogglingFavorite: toggleFavoriteMutation.isPending,
  };
}
